/**
 * NotesModal — a proper modal for actions that need a short written reason
 * (resolve, dismiss). Replaces the old window.prompt() flow so the user gets
 * a real textarea, a visible requirement, and keyboard escape.
 */
import React, { useState, useEffect, useRef } from 'react';

export default function NotesModal({ open, title, description, placeholder, confirmLabel, tone = 'orange', onConfirm, onClose }) {
  const [notes, setNotes] = useState('');
  const areaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setNotes('');
      setTimeout(() => areaRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const toneBtn = tone === 'red'
    ? 'bg-red-600 hover:bg-red-700'
    : tone === 'emerald'
      ? 'bg-emerald-600 hover:bg-emerald-700'
      : 'bg-orange-600 hover:bg-orange-700';

  const canConfirm = notes.trim().length >= 5;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg p-6">
        <h3 className="text-base font-bold text-slate-800">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        <textarea
          ref={areaRef}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 outline-none resize-none"
        />
        <p className="text-[11px] text-slate-400 mt-1.5">A written reason is required and is kept in the audit trail.</p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => canConfirm && onConfirm(notes.trim())}
            disabled={!canConfirm}
            className={`px-4 py-2 rounded-xl text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${toneBtn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
